import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  buildDashboard,
  currentWindowStart,
  windowStart,
  type DailyEventRow,
} from "@/lib/adminAnalytics";

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */

const SB_URL   = import.meta.env.VITE_SUPABASE_URL  as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

interface TopProductRow {
  product_id:   string;
  product_name: string;
  event_count:  number;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

function formatEventType(t: string): string {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTrend(pct: number | null): { label: string; cls: string } | null {
  if (pct === null) return null;
  const sign = pct >= 0 ? "+" : "";
  return {
    label: `${sign}${pct.toFixed(0)}%`,
    cls:   pct >= 0 ? "text-green-400" : "text-red-400",
  };
}

// Consistent colour per event type — bar fills in the daily chart
const EVENT_BAR_CLS: Record<string, string> = {
  product_view:         "bg-blue-500",
  ar_session_started:   "bg-orange-500",
  ar_model_placed:      "bg-green-500",
  search_performed:     "bg-purple-500",
  find_near_you_tapped: "bg-teal-500",
  share_tapped:         "bg-yellow-500",
  retailer_viewed:      "bg-indigo-500",
  directions_tapped:    "bg-cyan-500",
  favorite_added:       "bg-pink-500",
  favorite_removed:     "bg-pink-400",
};

const EVENT_TEXT_CLS: Record<string, string> = {
  product_view:         "text-blue-400",
  ar_session_started:   "text-orange-400",
  ar_model_placed:      "text-green-400",
  search_performed:     "text-purple-400",
  find_near_you_tapped: "text-teal-400",
  share_tapped:         "text-yellow-400",
  retailer_viewed:      "text-indigo-400",
  directions_tapped:    "text-cyan-400",
  favorite_added:       "text-pink-400",
  favorite_removed:     "text-pink-300",
};

function barCls(eventType: string): string {
  return EVENT_BAR_CLS[eventType] ?? "bg-gray-500";
}

function textCls(eventType: string): string {
  return EVENT_TEXT_CLS[eventType] ?? "text-gray-400";
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                       */
/* ------------------------------------------------------------------ */

function BigStat({
  value,
  label,
  trend,
}: {
  value: number;
  label: string;
  trend?: { label: string; cls: string } | null;
}) {
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/60 px-6 py-5 text-center min-w-[140px]">
      <p className="text-4xl font-bold tabular-nums text-white">{value.toLocaleString()}</p>
      <p className="mt-1 text-sm text-gray-400">{label}</p>
      {trend && (
        <p className={`mt-1 text-xs font-medium ${trend.cls}`}>{trend.label} vs prev 30d</p>
      )}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-base font-semibold text-white">{children}</h2>
  );
}

/* ------------------------------------------------------------------ */
/* Main Page                                                            */
/* ------------------------------------------------------------------ */

export default function AdminAnalytics() {
  const { session } = useAuth();
  const jwt = session?.access_token;

  const [daily,       setDaily]       = useState<DailyEventRow[]>([]);
  const [topViewed,   setTopViewed]   = useState<TopProductRow[]>([]);
  const [topArPlaced, setTopArPlaced] = useState<TopProductRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  /* ── Fetch aggregates from session_events via admin-only RPCs ───────
     (migration 20261016000200). session_events is service-role only, so the
     browser can't query it directly; the RPCs check profiles.role = 'admin'
     server-side and return counts only. Current 30 UTC days + the previous 30
     for the trend; top-5 lists use the current 30 days. */
  useEffect(() => {
    if (!jwt) return;
    const now = new Date();

    function rpc<T>(name: string, body: Record<string, unknown>): Promise<T[]> {
      return fetch(`${SB_URL}/rest/v1/rpc/${name}`, {
        method: "POST",
        headers: {
          apikey: ANON_KEY,
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      }).then((r) =>
        r.ok
          ? (r.json() as Promise<T[]>)
          : r.json().then((e: { message?: string }) => {
              throw new Error(e.message ?? `Error ${r.status}`);
            }),
      );
    }

    const currentSince = currentWindowStart(now).toISOString();
    setLoading(true);
    setError(null);
    Promise.all([
      rpc<DailyEventRow>("admin_analytics_daily_events", { p_since: windowStart(now).toISOString() }),
      rpc<TopProductRow>("admin_analytics_top_products", { p_event_type: "product_view",    p_since: currentSince, p_limit: 5 }),
      rpc<TopProductRow>("admin_analytics_top_products", { p_event_type: "ar_model_placed", p_since: currentSince, p_limit: 5 }),
    ])
      .then(([d, viewed, placed]) => {
        setDaily(d);
        setTopViewed(viewed);
        setTopArPlaced(placed);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Failed to load analytics"),
      )
      .finally(() => setLoading(false));
  }, [jwt]);

  /* ── Totals, trend, breakdown, 14-day chart (pure — see lib/adminAnalytics) ── */
  const dash = useMemo(() => buildDashboard(daily, new Date()), [daily]);

  const totalCurrent = dash.totalCurrent;
  const totalTrend   = formatTrend(dash.totalTrendPct);

  const eventBreakdown = useMemo(
    () => dash.breakdown.map((b) => ({
      type: b.type,
      count: b.count,
      prevCount: b.prevCount,
      trend: formatTrend(b.trendPct),
    })),
    [dash],
  );

  const dailyChart = useMemo(
    () => dash.chart.map((c) => ({
      ...c,
      label: new Date(`${c.date}T12:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" }),
    })),
    [dash],
  );

  const maxDaily = useMemo(
    () => Math.max(...dailyChart.map((d) => d.total), 1),
    [dailyChart],
  );

  /* ── Render ──────────────────────────────────────────────────────── */
  return (
    <div className="space-y-8">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">Analytics</h1>
        <p className="mt-0.5 text-sm text-gray-400">
          Engagement metrics — last 30 days compared with the prior 30-day period (UTC days, from the
          unified analytics pipeline).
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-800 bg-red-950/40 px-4 py-3">
          <p className="text-sm font-medium text-red-400">Failed to load analytics</p>
          <p className="mt-0.5 text-xs text-red-500">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="rounded-xl border border-gray-800 py-16 text-center text-sm text-gray-500">
          Loading analytics…
        </div>
      ) : (
        <>
          {/* ── Section 6 — Engagement Metrics ── */}
          <section className="space-y-5">
            <SectionTitle>Engagement Metrics</SectionTitle>

            {/* ── Total events — big number ── */}
            <div className="flex flex-wrap gap-4">
              <BigStat
                value={totalCurrent}
                label="Total events (30d)"
                trend={totalTrend}
              />
              <BigStat
                value={dash.currentByType["product_view"] ?? 0}
                label="Product views"
              />
              <BigStat
                value={dash.currentByType["ar_session_started"] ?? 0}
                label="AR sessions"
              />
              <BigStat
                value={dash.currentByType["ar_model_placed"] ?? 0}
                label="Models placed"
              />
            </div>

            {/* ── Event breakdown table ── */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-400">Event Breakdown</h3>
              <div className="overflow-x-auto rounded-xl border border-gray-800">
                <table className="w-full text-sm">
                  <thead className="bg-gray-900/60">
                    <tr className="border-b border-gray-800">
                      <th className="px-4 py-3 text-left font-medium text-gray-400">Event Type</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-400">Count (30d)</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-400">Prev 30d</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-400">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventBreakdown.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-gray-600 text-sm">
                          No events in the last 30 days
                        </td>
                      </tr>
                    ) : (
                      eventBreakdown.map(({ type, count, prevCount, trend }) => (
                        <tr key={type} className="border-t border-gray-800 hover:bg-gray-900/40 transition">
                          <td className="px-4 py-3">
                            <span className={`font-medium ${textCls(type)}`}>
                              {formatEventType(type)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-white font-semibold">
                            {count.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-500">
                            {prevCount.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {trend ? (
                              <span className={`text-xs font-medium ${trend.cls}`}>{trend.label}</span>
                            ) : (
                              <span className="text-xs text-gray-600">—</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Top products (2-column) ── */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Top 5 most viewed */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-400">Top 5 Most Viewed Products</h3>
                <div className="rounded-xl border border-gray-800 overflow-hidden">
                  {topViewed.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-600">No views yet</div>
                  ) : (
                    topViewed.map(({ product_id, product_name: name, event_count: count }, idx) => (
                      <div
                        key={product_id}
                        className="flex items-center justify-between px-4 py-3 border-b border-gray-800 last:border-b-0 hover:bg-gray-900/40 transition"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xs font-medium text-gray-600 w-4 shrink-0">
                            {idx + 1}
                          </span>
                          <span className="text-sm text-white truncate">{name}</span>
                        </div>
                        <span className="ml-3 text-sm tabular-nums font-semibold text-blue-400 shrink-0">
                          {count.toLocaleString()}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Top 5 most AR-placed */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-400">Top 5 AR-Placed Products</h3>
                <div className="rounded-xl border border-gray-800 overflow-hidden">
                  {topArPlaced.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-600">No placements yet</div>
                  ) : (
                    topArPlaced.map(({ product_id, product_name: name, event_count: count }, idx) => (
                      <div
                        key={product_id}
                        className="flex items-center justify-between px-4 py-3 border-b border-gray-800 last:border-b-0 hover:bg-gray-900/40 transition"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xs font-medium text-gray-600 w-4 shrink-0">
                            {idx + 1}
                          </span>
                          <span className="text-sm text-white truncate">{name}</span>
                        </div>
                        <span className="ml-3 text-sm tabular-nums font-semibold text-green-400 shrink-0">
                          {count.toLocaleString()}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* ── Daily event chart — last 14 days ── */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-400">Daily Events — Last 14 Days</h3>
              <div className="rounded-xl border border-gray-800 bg-gray-900/40 px-4 py-4">
                {/* Bars */}
                <div className="flex items-end gap-1.5 h-24">
                  {dailyChart.map(({ date, label, total, dominant }) => {
                    const heightPct = total === 0 ? 0 : Math.max((total / maxDaily) * 100, 4);
                    return (
                      <div
                        key={date}
                        className="group flex flex-1 flex-col items-center gap-1"
                        title={`${label}: ${total} events`}
                      >
                        <div
                          className={`w-full rounded-t transition-all ${barCls(dominant)} opacity-80 group-hover:opacity-100`}
                          style={{ height: `${heightPct}%` }}
                        />
                      </div>
                    );
                  })}
                </div>
                {/* X-axis labels — show every 2nd to avoid crowding */}
                <div className="flex gap-1.5 mt-1.5">
                  {dailyChart.map(({ date, label }, i) => (
                    <div key={date} className="flex flex-1 justify-center">
                      <span className="text-[9px] text-gray-600 truncate">
                        {i % 2 === 0 ? label : ""}
                      </span>
                    </div>
                  ))}
                </div>
                {/* Legend */}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-gray-500">
                  {Object.entries(EVENT_BAR_CLS).map(([type, cls]) => (
                    <span key={type} className="flex items-center gap-1">
                      <span className={`inline-block h-2 w-2 rounded-sm ${cls}`} />
                      {formatEventType(type)}
                    </span>
                  ))}
                </div>
              </div>
            </div>

          </section>
        </>
      )}
    </div>
  );
}
